import { ReactNode, useRef, useEffect, useState, useCallback } from "react";
import { Styles } from "./styled";
import { Tooltip } from "antd";

interface IProps {
  children: ReactNode;
  textTooltip?: string | ReactNode;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  style?: React.CSSProperties;
  isTextMultipleLine?: boolean;
}

const TextToolTip = ({
  children,
  textTooltip,
  className,
  onClick,
  style = {},
  isTextMultipleLine,
}: IProps) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [isOverflow, setIsOverflow] = useState(false);
  const [content, setContent] = useState<ReactNode>(children);

  const checkOverflow = useCallback(() => {
    if (isTextMultipleLine) return;
    const el = divRef.current;
    if (el) {
      const hasOverflow = el.scrollWidth > el.clientWidth;
      setIsOverflow(hasOverflow);
    }
  }, [isTextMultipleLine]);

  useEffect(() => {
    if (typeof children === "string" && isTextMultipleLine) {
      const arrayTexts = children.split("\n");
      if (arrayTexts.length > 1) {
        setIsOverflow(true);
        setContent(arrayTexts[0] + "...");
      }
    } else {
      setContent(children);
    }
  }, [children, isTextMultipleLine]);

  useEffect(() => {
    const el = divRef.current;
    if (!el) return;

    checkOverflow();
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(el);
    const handleResize = () => checkOverflow();
    window.addEventListener("resize", handleResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, [checkOverflow]);

  useEffect(() => {
    checkOverflow();
  }, [children, checkOverflow]);

  return (
    <Tooltip
      open={isOverflow ? undefined : false}
      title={
        <div style={{ whiteSpace: "pre-wrap" }}>{textTooltip || children}</div>
      }
      placement="top"
      getPopupContainer={() => document.body}
      overlayStyle={{
        zIndex: 10000,
        margin: 0,
        pointerEvents: "none",
      }}
      overlayInnerStyle={{
        pointerEvents: "none",
      }}
      align={{
        offset: [0, 12],
      }}
      mouseEnterDelay={0.2}
      mouseLeaveDelay={0.1}
      destroyTooltipOnHide
      trigger={["hover"]}
    >
      <Styles
        style={style}
        ref={divRef}
        className={className || ""}
        onClick={(e) => {
          e.preventDefault();
          onClick && onClick?.(e);
        }}
      >
        {content}
      </Styles>
    </Tooltip>
  );
};

export default TextToolTip;
